import React, { useState, useEffect } from 'react';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { 
  EmergencyContact, 
  CreateEmergencyContactRequest,
  UpdateEmergencyContactRequest,
  EmergencyContactAccessLevel
} from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Modal } from '../common/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Phone, 
  Plus, 
  Edit, 
  Trash2, 
  Mail, 
  User,
  Shield,
  AlertTriangle,
  RefreshCw,
  Heart,
  Stethoscope,
  FileText
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface EmergencyContactsProps {
  onRefresh: () => void;
}

const EmergencyContacts: React.FC<EmergencyContactsProps> = ({ onRefresh }) => {
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<EmergencyContact | null>(null);
  const [formData, setFormData] = useState<CreateEmergencyContactRequest>({
    name: '',
    email: '',
    phone: '',
    relationship: '',
    access_level: 'medical_emergency'
  });

  const accessLevels = [
    { 
      value: 'medical_emergency', 
      label: 'Medical Emergency Access', 
      description: 'Access to medical information, medications, allergies, and emergency contacts in case of medical emergency',
      icon: <Stethoscope className="w-4 h-4" />,
      color: 'bg-red-100 text-red-800'
    },
    { 
      value: 'basic_contact', 
      label: 'Basic Contact Info', 
      description: 'Access to basic contact information and emergency procedures only',
      icon: <Phone className="w-4 h-4" />,
      color: 'bg-green-100 text-green-800'
    },
    { 
      value: 'limited_documents', 
      label: 'Limited Documents', 
      description: 'Access to emergency documents, insurance info, and basic medical records',
      icon: <FileText className="w-4 h-4" />,
      color: 'bg-yellow-100 text-yellow-800'
    }
  ];

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await TrustedAdvisorService.getEmergencyContacts();
      setContacts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load emergency contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim() && !formData.phone.trim()) {
      setError('Either email or phone is required');
      return;
    }
    if (!formData.relationship.trim()) {
      setError('Relationship is required');
      return;
    }

    try {
      await TrustedAdvisorService.createEmergencyContact(formData);
      await loadContacts();
      onRefresh();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create emergency contact');
    }
  };

  const handleEditContact = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedContact) return;

    try {
      await TrustedAdvisorService.updateEmergencyContact({
        contact_id: selectedContact.id,
        ...formData
      });
      await loadContacts();
      onRefresh();
      setShowEditModal(false);
      setSelectedContact(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update emergency contact');
    }
  };

  const handleRemoveContact = async (contactId: string) => {
    if (!confirm('Are you sure you want to remove this emergency contact?')) {
      return;
    }

    try {
      await TrustedAdvisorService.updateEmergencyContact({
        contact_id: contactId,
        is_active: false
      });
      await loadContacts();
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove emergency contact');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      relationship: '',
      access_level: 'medical_emergency'
    });
    setError(null);
  };

  const openEditModal = (contact: EmergencyContact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      relationship: contact.relationship,
      access_level: contact.access_level
    });
    setShowEditModal(true);
  };

  const getAccessLevelColor = (level: EmergencyContactAccessLevel) => {
    const accessLevel = accessLevels.find(a => a.value === level);
    return accessLevel?.color || 'bg-gray-100 text-gray-800';
  };

  const getAccessLevelIcon = (level: EmergencyContactAccessLevel) => {
    const accessLevel = accessLevels.find(a => a.value === level);
    return accessLevel?.icon || <Shield className="w-4 h-4" />;
  };

  const getAccessLevelDescription = (level: EmergencyContactAccessLevel) => {
    const accessLevel = accessLevels.find(a => a.value === level);
    return accessLevel?.description || 'No description available';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Emergency Contacts</h2>
          <p className="text-gray-600 mt-1">
            Manage emergency contacts with limited access to your information
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button onClick={loadContacts} variant="outline" className="flex items-center">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowAddModal(true)} 
            variant="primary" 
            className="flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <p className="text-red-700">{error}</p>
        </Card>
      )}

      {/* Information Box */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">
              About Emergency Contacts
            </h4>
            <p className="text-sm text-blue-800">
              Emergency contacts are different from trusted advisors. They have limited access and are typically 
              people like neighbors, teachers, or colleagues who may need basic information in emergency situations.
              Medical emergency access allows contacts to view critical medical information when needed.
            </p>
          </div>
        </div>
      </Card>

      {/* Access Levels Information */}
      <Card className="p-6 bg-gray-50 border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Emergency Access Levels</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accessLevels.map((level) => (
            <div key={level.value} className="p-3 bg-white rounded-md border">
              <div className="flex items-center mb-2">
                <div className={`p-1 rounded mr-2 ${level.color}`}>
                  {level.icon}
                </div>
                <span className="text-sm font-medium text-gray-900">{level.label}</span>
              </div>
              <p className="text-xs text-gray-600">{level.description}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Contacts List */}
      <div className="space-y-4">
        {contacts.length === 0 ? (
          <Card className="p-12 text-center">
            <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No emergency contacts yet
            </h3>
            <p className="text-gray-500 mb-4">
              Add emergency contacts who may need limited access to your information in emergency situations.
            </p>
            <Button onClick={() => setShowAddModal(true)} variant="primary">
              Add First Contact
            </Button>
          </Card>
        ) : (
          contacts.map((contact) => (
            <Card key={contact.id} className="p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-semibold text-lg">
                      {contact.name[0]}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {contact.name}
                      </h3>
                      <Badge className={getAccessLevelColor(contact.access_level)}>
                        <div className="flex items-center space-x-1">
                          {getAccessLevelIcon(contact.access_level)}
                          <span className="capitalize">{contact.access_level.replace('_', ' ')}</span>
                        </div>
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{contact.relationship}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {contact.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          <span>{contact.email}</span>
                        </div>
                      )}
                      
                      {contact.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center">
                        <span>Added {formatDistanceToNow(new Date(contact.created_at), { addSuffix: true })}</span>
                      </div>
                    </div>

                    {/* Access Level Description */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-600">
                        <strong>Access Level:</strong> {getAccessLevelDescription(contact.access_level)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(contact)}
                    className="flex items-center"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveContact(contact.id)}
                    className="flex items-center text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Contact Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} size="lg">
        <div className="p-6">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
              <Phone className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Add Emergency Contact
              </h2>
              <p className="text-sm text-gray-600">
                Add someone who may need limited access in emergency situations
              </p>
            </div>
          </div>

          <form onSubmit={handleAddContact} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship *
                </label>
                <Input
                  type="text"
                  value={formData.relationship}
                  onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                  placeholder="e.g., Neighbor, Friend, Teacher, Doctor, etc."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe your relationship (e.g., "Next door neighbor", "My doctor", "Work colleague")
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Access Level *
              </label>
              <Select
                value={formData.access_level}
                onValueChange={(value) => setFormData(prev => ({ ...prev, access_level: value as EmergencyContactAccessLevel }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent>
                  {accessLevels.map(level => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {getAccessLevelDescription(formData.access_level)}
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Add Contact
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Edit Contact Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} size="lg">
        <div className="p-6">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <Edit className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Emergency Contact
              </h2>
              <p className="text-sm text-gray-600">
                Update contact information and access level
              </p>
            </div>
          </div>

          <form onSubmit={handleEditContact} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship *
                </label>
                <Input
                  type="text"
                  value={formData.relationship}
                  onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                  placeholder="e.g., Neighbor, Friend, Teacher, Doctor, etc."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe your relationship (e.g., "Next door neighbor", "My doctor", "Work colleague")
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Access Level *
              </label>
              <Select
                value={formData.access_level}
                onValueChange={(value) => setFormData(prev => ({ ...prev, access_level: value as EmergencyContactAccessLevel }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent>
                  {accessLevels.map(level => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {getAccessLevelDescription(formData.access_level)}
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedContact(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default EmergencyContacts; 